import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';

/**
 * Get connection string from environment variables
 * Supports both the React-prefixed version and the original cloud variable name
 * This wrapper pattern allows sharing env variables between backend and frontend
 */
function getConnectionString(): string {
  if (process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING) {
    return process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING;
  }

  if (process.env.APPLICATIONINSIGHTS_CONNECTION_STRING) {
    return process.env.APPLICATIONINSIGHTS_CONNECTION_STRING;
  }

  return '';
}

const connectionString = getConnectionString();

// Initialize the Application Insights instance for the browser only if connection string is available
let appInsights: ApplicationInsights | null = null;

if (connectionString) {
  appInsights = new ApplicationInsights({
    config: {
      connectionString: connectionString,
      enableAutoRouteTracking: true, // For single-page applications (SPAs)
      maxAjaxCallsPerView: -1, // Monitor all AJAX calls per page view (default is 500)
      // enableCorsCorrelation: true, // Enable CORS correlation
      // SECURITY FIX: Disabled header tracking to prevent leaking Authorization tokens, cookies, and sensitive headers
      enableRequestHeaderTracking: false, // DO NOT track request headers (contains Authorization: Bearer tokens)
      enableResponseHeaderTracking: false, // DO NOT track response headers (may contain Set-Cookie)
    },
  });

  appInsights.loadAppInsights();

  // Add telemetry initializer to strip any remaining sensitive headers/data
  appInsights.addTelemetryInitializer((envelope) => {
    const data = envelope.data?.baseData as any;

    // Scrub sensitive headers from AJAX call properties
    if (envelope.name === 'RemoteDependencyData' && data?.properties) {
      const sensitiveHeaders = ['authorization', 'cookie', 'set-cookie', 'x-auth-token', 'x-api-key'];
      Object.keys(data.properties).forEach(key => {
        if (sensitiveHeaders.includes(key.toLowerCase())) {
          data.properties[key] = '[REDACTED]';
        }
      });
    }

    return true;
  });

  appInsights.trackPageView();
} else {
} 

/**
 * Redact sensitive patterns from a string
 * Removes tokens, credentials, and PII before logging to telemetry
 */
function redactSensitivePatterns(text: string): string {
  if (!text || typeof text !== 'string') return text;

  // Define patterns to redact
  const patterns = [
    // Bearer tokens: "Bearer eyJ..." -> "Bearer [REDACTED]"
    /Bearer\s+[A-Za-z0-9\-_.]+/gi,
    // API keys and secrets
    /(api[_-]?key|client_secret|access_token|refresh_token)\s*[:=]\s*[^\s,}]+/gi,
    // Basic auth: "user:password" -> "[REDACTED]"
    /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+/g, // email addresses
    // Common credential patterns
    /(password|pwd|secret|token|authorization)\s*[:=]\s*[^\s,}]+/gi,
  ];

  let redacted = text;
  patterns.forEach(pattern => {
    redacted = redacted.replace(pattern, '[REDACTED]');
  });

  return redacted;
}

/**
 * Safe stringify that redacts sensitive data
 */
function safeStringify(args: any[]): string {
  if (!args || args.length === 0) return '';

  return args
    .map(arg => {
      try {
        const str = typeof arg === 'string' ? arg : JSON.stringify(arg);
        return redactSensitivePatterns(str);
      } catch {
        return typeof arg;
      }
    })
    .slice(0, 3) // Only use first 3 args to keep message short
    .join(' ')
    .substring(0, 500); // Limit message length
}

/**
 * Initialize console wrapper to capture only critical console messages and send them to Application Insights
 * SECURITY FIX: Redacts sensitive data (tokens, credentials, emails) before forwarding to telemetry
 * Only tracks console.error and console.warn to reduce noise and excessive telemetry calls
 * Implements rate limiting to prevent excessive calls to Application Insights
 */
export function initializeConsoleWrapper() {
  // Skip if Application Insights is not initialized
  if (!appInsights) {
    return;
  }

  // Rate limiting: track the last time we sent a trace
  let lastTraceTime = 0;
  const RATE_LIMIT_MS = 1000; // Only allow one trace per second

  // Only track error and warn, not log/info/debug to reduce noise
  const criticalMethods = ['error', 'warn'] as const;

  criticalMethods.forEach((method) => {
    const originalMethod = console[method];

    // Override the console function
    console[method] = (...args: any[]) => {
      // Call the original console method first to maintain expected behavior
      originalMethod(...args);

      // Apply rate limiting - only send to App Insights at most once per second
      const now = Date.now();
      if (now - lastTraceTime < RATE_LIMIT_MS) {
        return;
      }
      lastTraceTime = now;

      // Map console method to Application Insights SeverityLevel
      let severityLevel: SeverityLevel;
      switch (method) {
        case 'error':
          severityLevel = SeverityLevel.Error;
          break;
        case 'warn':
          severityLevel = SeverityLevel.Warning;
          break;
        default:
          severityLevel = SeverityLevel.Verbose;
          break;
      }

      // Send to Application Insights with redacted content
      try {
        const safeMessage = safeStringify(args);
        if (safeMessage) { // Only send if there's actual content
          appInsights?.trackTrace({
            message: `Console ${method}: ${safeMessage}`,
            severityLevel: severityLevel,
          });
        }
      } catch (e) {
        originalMethod('Failed to track console message to Application Insights:', e);
      }
    };
  });
}

export { appInsights };
